import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Building2, Users, Target, Globe2, Briefcase, GraduationCap, Network, LineChart, TrendingUp, Wallet, Users2 } from 'lucide-react';

export default function AboutPage() {
  const { theme } = useTheme();

  const stats = [
    { label: 'Investment', value: '€8.2B', description: 'Total investment committed' },
    { label: 'Developing Nations', value: '45+', description: 'Countries with active training programs' },
    { label: 'Cost Efficiency', value: '73%', description: 'Average training cost reduction' },
    { label: 'ROI', value: '312%', description: 'Average return on workforce investment' }
  ];

  const platforms = [
    {
      icon: <Network className="h-8 w-8 text-blue-500" />,
      name: 'GistUZone',
      url: 'gocity.me',
      description: 'Professional networking and social information-sharing platform fostering collaboration and knowledge exchange.',
      features: [
        'Industry insights and trends',
        'Professional networking',
        'Knowledge sharing',
        'Community engagement'
      ],
      color: 'blue'
    },
    {
      icon: <Building2 className="h-8 w-8 text-green-500" />,
      name: 'GoCity Workspace',
      url: 'gocityservice.com',
      description: 'Comprehensive workspace and lab service management platform for modern professionals.',
      features: [
        'Workspace booking',
        'Lab facilities',
        'Resource management',
        'Professional services'
      ],
      color: 'green'
    },
    {
      icon: <Briefcase className="h-8 w-8 text-purple-500" />,
      name: 'GoCity Job',
      url: 'gocityjob.com',
      description: 'Cutting-edge skill development and labor employment platform bridging talent with opportunities.',
      features: [
        'Skill development',
        'Job matching',
        'Career guidance',
        'Industry certifications'
      ],
      color: 'purple'
    },
    {
      icon: <LineChart className="h-8 w-8 text-red-500" />,
      name: 'GoCity Business',
      url: 'gocitybusiness.com',
      description: 'Enterprise platform for workforce development and business team building.',
      features: [
        'Team building',
        'Business training',
        'Workforce development',
        'Enterprise solutions'
      ],
      color: 'red'
    }
  ];

  const values = [
    {
      icon: <Target className="h-6 w-6 text-blue-500" />,
      title: 'Innovation',
      description: 'Pioneering solutions that shape the future of work and technology.'
    },
    {
      icon: <Users className="h-6 w-6 text-green-500" />,
      title: 'Empowerment',
      description: 'Enabling individuals and organizations to reach their full potential.'
    },
    {
      icon: <Globe2 className="h-6 w-6 text-purple-500" />,
      title: 'Global Impact',
      description: 'Creating positive change across borders and communities.'
    },
    {
      icon: <GraduationCap className="h-6 w-6 text-red-500" />,
      title: 'Continuous Learning',
      description: 'Fostering a culture of lifelong learning and development.'
    }
  ];

  const timeline = [
    {
      year: '2018',
      title: 'The Beginning',
      description: 'Founded by a group of students at Ca\' Foscari University of Venice, Italy, with a vision to transform the tech industry.'
    },
    {
      year: '2023',
      title: 'Strategic Vision',
      description: 'CEO identifies key challenges in tech employment, expertise retention, and professional development.'
    },
    {
      year: '2024',
      title: 'Launch & Growth',
      description: 'Assembled teams for tech training, programming, and business development, launching four specialized platforms.'
    },
    {
      year: '2030',
      title: 'Future Goal',
      description: 'Targeting to empower 273,333+ professionals globally through our comprehensive ecosystem.'
    }
  ];

  const investmentHighlights = [
    {
      icon: <Users2 className="h-8 w-8 text-blue-500" />,
      title: 'Talent Pool Expansion',
      description: 'Access to diverse, skilled professionals from developing nations, bringing fresh perspectives and innovation.',
      stat: '150,000+',
      statLabel: 'Available Talent'
    },
    {
      icon: <Wallet className="h-8 w-8 text-green-500" />,
      title: 'Cost Optimization',
      description: 'Significant reduction in training and development costs through economies of scale in developing regions.',
      stat: '73%',
      statLabel: 'Cost Reduction'
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-purple-500" />,
      title: 'Economic Impact',
      description: 'Contributing to economic growth in developing nations while building a sustainable talent ecosystem.',
      stat: '45+',
      statLabel: 'Partner Nations'
    }
  ];

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className={`text-4xl md:text-5xl font-bold mb-6 ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Shaping the Future of Work
          </h1>
          <p className={`text-xl mb-8 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Empowering global technology workforce development through strategic investments in developing nations
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`p-6 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                }`}
              >
                <div className={`text-3xl font-bold mb-2 ${
                  theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                }`}>
                  {stat.value}
                </div>
                <div className={`text-sm font-medium mb-1 ${
                  theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                }`}>
                  {stat.label}
                </div>
                <div className={`text-xs ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {stat.description}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Investment Focus Section */}
        <div className="mb-16">
          <h2 className={`text-3xl font-bold text-center mb-12 ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Why We Focus on Developing Nations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {investmentHighlights.map((highlight, index) => (
              <div
                key={index}
                className={`
                  p-6 rounded-lg text-center
                  ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
                `}
              >
                <div className="flex justify-center mb-4">
                  {highlight.icon}
                </div>
                <h3 className={`text-xl font-bold mb-2 ${
                  theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                }`}>
                  {highlight.title}
                </h3>
                <p className={`mb-4 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {highlight.description}
                </p>
                <div className={`text-2xl font-bold mb-1 ${
                  theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                }`}>
                  {highlight.stat}
                </div>
                <div className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {highlight.statLabel}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline Section */}
        <div className="mb-16">
          <h2 className={`text-3xl font-bold text-center mb-12 ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Our Journey
          </h2>
          <div className="relative">
            <div className={`absolute left-1/2 transform -translate-x-px h-full w-0.5 ${
              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
            }`} />
            <div className="space-y-12">
              {timeline.map((event, index) => (
                <div key={index} className={`relative ${index % 2 === 0 ? 'md:ml-auto md:pl-16' : 'md:mr-auto md:pr-16'}`}>
                  <div className={`
                    absolute left-1/2 transform -translate-x-1/2 -translate-y-4
                    w-8 h-8 rounded-full border-4
                    ${theme === 'dark'
                      ? 'bg-gray-800 border-blue-500'
                      : 'bg-white border-blue-600'
                    }
                  `} />
                  <div className={`
                    p-6 rounded-lg max-w-xl mx-auto md:mx-0
                    ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
                  `}>
                    <div className={`text-sm font-semibold mb-1 ${
                      theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                    }`}>
                      {event.year}
                    </div>
                    <h3 className={`text-lg font-bold mb-2 ${
                      theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      {event.title}
                    </h3>
                    <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                      {event.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Platforms Section */}
        <div className="mb-16">
          <h2 className={`text-3xl font-bold text-center mb-12 ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Our Platforms
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {platforms.map((platform, index) => (
              <div
                key={index}
                className={`
                  p-6 rounded-lg transition-transform hover:-translate-y-1
                  ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
                `}
              >
                <div className="flex items-start gap-4">
                  {platform.icon}
                  <div className="flex-1">
                    <h3 className={`text-xl font-bold mb-2 ${
                      theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      {platform.name}
                    </h3>
                    <a
                      href={`https://${platform.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      {platform.url}
                    </a>
                  </div>
                </div>
                <p className={`mt-4 mb-6 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {platform.description}
                </p>
                <ul className="space-y-2">
                  {platform.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className={`flex items-center space-x-2 text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      <div className={`h-1.5 w-1.5 rounded-full bg-${platform.color}-500`} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Values Section */}
        <div className="mb-16">
          <h2 className={`text-3xl font-bold text-center mb-12 ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Our Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className={`
                  p-6 rounded-lg text-center
                  ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
                `}
              >
                <div className="flex justify-center mb-4">
                  {value.icon}
                </div>
                <h3 className={`text-lg font-bold mb-2 ${
                  theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                }`}>
                  {value.title}
                </h3>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className={`
          max-w-3xl mx-auto text-center p-8 rounded-lg
          ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
        `}>
          <h2 className={`text-2xl font-bold mb-4 ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Join Us in Shaping the Future
          </h2>
          <p className={`mb-8 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Be part of our mission to transform the global technology workforce through strategic investments in developing nations.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link
              to="/contact"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Contact Us
            </Link>
            <Link
              to="/careers"
              className={`
                px-6 py-3 rounded-lg transition-colors
                ${theme === 'dark'
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              View Careers
            </Link>
            <Link
              to="/blog"
              className={`
                px-6 py-3 rounded-lg transition-colors
                ${theme === 'dark'
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              Read Our Blog
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}